import { useEffect, useMemo, useState } from 'react';
import {
  IconPlus, IconPencil, IconAlertCircle, IconCheck, IconQrcode,
  IconUsers, IconMapPin, IconLink, IconPrinter, IconBan, IconCircleCheck,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

interface ApiTable {
  id: string;
  name: string;
  code: string;
  capacity: number;
  area: string | null;
  notes: string | null;
  sortOrder: number;
  isActive: boolean;
}

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function qrUrl(code: string): string {
  // Use the goqr.me API — free, no key, returns a PNG via GET.
  const target = `${window.location.origin}/t/${code}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=4&data=${encodeURIComponent(target)}`;
}

export default function AdminTablesPage() {
  const [tables, setTables] = useState<ApiTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ApiTable | 'new' | null>(null);
  const [printing, setPrinting] = useState<ApiTable[] | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/api/tables?includeInactive=1', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setTables(data.tables ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function toggleActive(t: ApiTable) {
    const next = !t.isActive;
    setTables((prev) => prev.map((x) => x.id === t.id ? { ...x, isActive: next } : x));
    try {
      const r = await fetch(`/api/tables/${t.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      setTables((prev) => prev.map((x) => x.id === t.id ? { ...x, isActive: !next } : x));
      setError((err as Error).message);
    }
  }

  const grouped = useMemo(() => {
    const m = new Map<string, ApiTable[]>();
    for (const t of tables) {
      const key = t.area?.trim() || 'No area';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(t);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [tables]);

  const activeCount = tables.filter((t) => t.isActive).length;

  return (
    <AdminLayout
      title="Tables"
      subtitle={`${activeCount} active · ${tables.length} total · QR codes auto-generated per table`}
      actions={
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            disabled={tables.length === 0}
            onClick={() => setPrinting(tables.filter((t) => t.isActive))}
            className="h-10 px-3 inline-flex items-center gap-2 rounded-md bg-light border-2 border-border text-13 font-bold text-heading hover:border-primary disabled:opacity-50"
          >
            <IconPrinter size={14} stroke={2.5} />
            Print QR sheet
          </button>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
          >
            <IconPlus size={16} stroke={2.5} />
            New table
          </button>
        </div>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {loading ? (
        <p className="text-13 italic text-gray-700">Loading…</p>
      ) : tables.length === 0 ? (
        <section className="rounded-lg bg-surface border border-border p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
            <IconQrcode size={22} stroke={2} />
          </div>
          <p className="text-14 font-bold text-heading">No tables yet</p>
          <p className="text-13 font-medium text-gray-700 mt-1">Add your first table — each one gets a QR code that opens the menu on a customer's phone.</p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="mt-4 h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
          >
            <IconPlus size={16} stroke={2.5} />
            Add the first table
          </button>
        </section>
      ) : (
        <div className="space-y-6">
          {grouped.map(([area, ts]) => (
            <section key={area} className="rounded-lg bg-surface border border-border p-4">
              <div className="flex items-center gap-2 mb-3">
                <IconMapPin size={16} stroke={2.25} className="text-primary" />
                <h2 className="text-15 font-bold text-heading">{area}</h2>
                <span className="text-12 font-semibold text-gray-700">· {ts.length} tables</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                {ts.map((t) => (
                  <li key={t.id} className={`rounded-lg border-2 ${t.isActive ? 'border-border bg-light' : 'border-dashed border-border opacity-60'} p-3 flex items-center gap-3`}>
                    <img src={qrUrl(t.code)} alt={`QR for ${t.name}`} className="w-16 h-16 rounded bg-[#FFFFFF] p-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-14 font-bold text-heading truncate">{t.name}</p>
                      <p className="text-12 font-medium text-gray-700 truncate">/t/{t.code}</p>
                      <div className="inline-flex items-center gap-1 mt-1 text-11 font-bold uppercase tracking-wider text-gray-700">
                        <IconUsers size={12} stroke={2.5} /> {t.capacity}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <a
                        href={`/t/${t.code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md border-2 border-border text-gray-700 hover:border-primary hover:text-primary"
                        title="Open as customer"
                      >
                        <IconLink size={14} stroke={2.25} />
                      </a>
                      <button
                        type="button"
                        onClick={() => void toggleActive(t)}
                        className={`w-8 h-8 inline-flex items-center justify-center rounded-md border-2 ${t.isActive ? 'border-danger text-danger hover:bg-danger hover:text-[#FFFFFF]' : 'border-success text-success hover:bg-success hover:text-[#FFFFFF]'}`}
                        title={t.isActive ? 'Deactivate' : 'Reactivate'}
                      >
                        {t.isActive ? <IconBan size={14} stroke={2.25} /> : <IconCircleCheck size={14} stroke={2.25} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(t)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md border-2 border-border text-gray-700 hover:border-primary hover:text-primary"
                        title="Edit"
                      >
                        <IconPencil size={14} stroke={2.25} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <TableFormModal
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { void load(); setEditing(null); }}
      />
      {printing && <QrSheetPrintView tables={printing} onClose={() => setPrinting(null)} />}
    </AdminLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Form                                                                        */
/* -------------------------------------------------------------------------- */

function TableFormModal({
  target, onClose, onSaved,
}: {
  target: ApiTable | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [capacity, setCapacity] = useState(4);
  const [area, setArea] = useState('');
  const [notes, setNotes] = useState('');
  const [sortOrder, setSortOrder] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setName(''); setCode(''); setCapacity(4); setArea(''); setNotes(''); setSortOrder(100); setIsActive(true);
    } else {
      setName(target.name); setCode(target.code); setCapacity(target.capacity);
      setArea(target.area ?? ''); setNotes(target.notes ?? '');
      setSortOrder(target.sortOrder); setIsActive(target.isActive);
    }
    setErr(null);
  }, [target]);

  useEffect(() => {
    if (isNew && name) setCode(slugify(name));
  }, [name, isNew]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const url = isNew ? '/api/tables' : `/api/tables/${existing!.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, code, capacity, area: area || null, notes: notes || null, sortOrder, isActive }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} size="md" title={isNew ? 'New table' : `Edit · ${existing?.name ?? ''}`}>
      <div className="space-y-4">
        <Field label="Name" required>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Table 1"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
        </Field>
        <Field label="Code" helper="URL slug for /t/:code — auto-filled from name">
          <input type="text" value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="table-1"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-mono text-heading" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Seats">
            <input type="number" value={capacity} onChange={(e) => setCapacity(Math.max(1, Number(e.target.value) || 1))}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading" />
          </Field>
          <Field label="Sort order">
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading" />
          </Field>
        </div>
        <Field label="Area" helper="Optional — group tables (e.g. Inside / Patio)">
          <input type="text" value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. Inside"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
        </Field>
        <Field label="Notes" helper="Optional — visible only to admin">
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder=""
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading" />
        </Field>
        <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md bg-light border border-border-soft">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4" />
          <span>
            <span className="text-14 font-bold text-heading block">Active</span>
            <span className="text-12 font-medium text-gray-700 block">Customers can scan and view the menu</span>
          </span>
        </label>

        {err && <p className="text-13 font-semibold text-danger">{err}</p>}

        <div className="flex items-stretch gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving || !name.trim() || !code.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : (isNew ? 'Create table' : 'Save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, helper, required, children }: { label: string; helper?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}{required && <span className="text-danger ml-1">*</span>}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      {children}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Printable QR sheet — opens a new window with a print-ready layout           */
/* -------------------------------------------------------------------------- */

function QrSheetPrintView({ tables, onClose }: { tables: ApiTable[]; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} size="xl" title={`QR codes — ${tables.length} tables`}>
      <p className="text-13 text-gray-700 mb-3">Scroll to preview · click Print for an A4-friendly sheet (4 per page).</p>
      <div className="rounded-md bg-light border border-border-soft p-4 max-h-[60vh] overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tables.map((t) => (
            <div key={t.id} className="rounded-lg bg-[#FFFFFF] border-2 border-border p-4 text-center">
              <img src={qrUrl(t.code)} alt={`QR for ${t.name}`} className="w-40 h-40 mx-auto" />
              <p className="text-18 font-bold text-heading mt-2">{t.name}</p>
              <p className="text-12 font-mono text-gray-700">/t/{t.code}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-stretch gap-2 pt-4">
        <button onClick={onClose} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Close</button>
        <button onClick={() => window.print()} className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover">
          <IconPrinter size={16} stroke={2.5} />
          Print
        </button>
      </div>
    </Modal>
  );
}
